/**
 * The keyboard-shortcut badge for chart chrome — the trailing key hint on a
 * right-click menu row (`contextMenuChrome`) and on a DrawingBar flyout item.
 * One source of truth so the two never drift in metrics or glyph alignment.
 *
 * The `⇧` token renders as a solid FILLED up-arrow rather than the hollow
 * unicode glyph, which clashes with the solid letter beside it. The viewBox is
 * trimmed to the arrow's own bounds so it optically centres on the letter's
 * cap-height rather than floating high in a padded 24×24 box.
 *
 * Standalone by design: styles off the chart CSS variables (`--chart-text`),
 * with no shadcn or app-level dependency, so charts-core stays self-contained.
 * Call sites tune placement via `className` (e.g. `ml-auto pl-3` on a menu row).
 */
export function HotkeyBadge({ hotkey, className = '' }: { hotkey: string; className?: string }) {
  // Tokenize on chord units, not characters: "Alt+Shift+R" renders as three
  // words and two joiners rather than eleven spaced glyphs.
  const tokens = hotkey.split(/(⇧|⌥|Alt|Shift|\+)/).filter(Boolean)
  return (
    <kbd
      aria-label={hotkey.replace('⇧', 'Shift ').replace('⌥', 'Option ')}
      className={
        'flex h-[15px] shrink-0 items-center gap-[3px] rounded border border-[hsl(var(--chart-text)/0.2)] px-1 text-[10px] font-medium leading-none text-[hsl(var(--chart-text-muted))] ' +
        className
      }
    >
      {tokens.map((ch, i) =>
        ch === '⇧' ? (
          <svg
            key={i}
            viewBox="4.5 3 15 15"
            fill="currentColor"
            aria-hidden="true"
            className="h-[8px] w-[8px]"
          >
            <path d="M12 3l7 8h-4v7h-6v-7H5z" />
          </svg>
        ) : (
          <span key={i}>{ch}</span>
        ),
      )}
    </kbd>
  )
}
